/**
 * OnboardingGuide — 5-step product tour that appears the first time a user
 * lands on the dashboard after completing sign-up.
 *
 * State is persisted in localStorage under `guide_seen_<officeId>` so it
 * never shows again once dismissed on any device session.
 */
import { useState, useEffect } from 'react'
import { ShieldLogo } from './ShieldLogo'
import { useLang } from '../contexts/LangContext'

// ─── Shared glow container ─────────────────────────────────────────────────────
// Each step: large centered icon on soft radial glow. No text in illustrations.

function IconFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center" style={{ height: 148 }}>
      <svg width="200" height="148" viewBox="0 0 200 148" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Outer glow */}
        <circle cx="100" cy="74" r="58" fill="rgba(29,158,117,0.06)" />
        {/* Inner glow */}
        <circle cx="100" cy="74" r="40" fill="rgba(29,158,117,0.10)" />
        {children}
      </svg>
    </div>
  )
}

// ─── Step 1: Welcome — official ShieldLogo with ripple ────────────────────────

function IllustrationWelcome() {
  return (
    <div className="flex items-center justify-center" style={{ height: 148 }}>
      <div className="relative flex items-center justify-center" style={{ width: 148, height: 148 }}>
        {[0, 0.8, 1.6, 2.4].map((delay, i) => (
          <span key={i} className="ripple-ring" style={{ animationDelay: `${delay}s`, opacity: 0 }} />
        ))}
        <ShieldLogo size={100} />
      </div>
    </div>
  )
}

// ─── Step 2: Add client — person + plus badge ─────────────────────────────────

function IllustrationAddClient() {
  return (
    <IconFrame>
      {/* Person head */}
      <circle cx="100" cy="62" r="16" stroke="#1D9E75" strokeWidth="2.5" fill="rgba(29,158,117,0.15)" />
      {/* Person body arc */}
      <path d="M68 102 C68 86 78 78 100 78 C122 78 132 86 132 102" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Plus badge — top right of person */}
      <circle cx="122" cy="50" r="12" fill="#1D9E75" />
      <path d="M122 44 L122 56 M116 50 L128 50" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    </IconFrame>
  )
}

// ─── Step 3: Recipient Number — document + clock warning ─────────────────────

function IllustrationRecipient() {
  return (
    <IconFrame>
      {/* Document */}
      <rect x="76" y="42" width="48" height="58" rx="6" fill="rgba(29,158,117,0.15)" stroke="#1D9E75" strokeWidth="2" />
      {/* Fold corner */}
      <path d="M112 42 L124 54" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M112 42 L112 54 L124 54" fill="rgba(29,158,117,0.25)" stroke="#1D9E75" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Hash / # symbol */}
      <path d="M91 65 L93 79 M99 65 L101 79 M88 69.5 L104 69.5 M87.5 74.5 L103.5 74.5"
        stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" />
      {/* Clock badge — amber, bottom right */}
      <circle cx="124" cy="94" r="13" fill="#0A1810" />
      <circle cx="124" cy="94" r="11" fill="rgba(245,158,11,0.15)" stroke="#D97706" strokeWidth="1.5" />
      <path d="M124 88 L124 94 L129 94" stroke="#D97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </IconFrame>
  )
}

// ─── Step 4: Dashboard — status list with color dots ─────────────────────────

function IllustrationDashboard() {
  return (
    <IconFrame>
      {/* Card */}
      <rect x="56" y="40" width="88" height="68" rx="8" fill="rgba(29,158,117,0.08)" stroke="rgba(29,158,117,0.3)" strokeWidth="1.5" />
      {/* Row 1 — green */}
      <circle cx="70" cy="60" r="5" fill="#1D9E75" />
      <rect x="80" y="56" width="50" height="8" rx="3" fill="rgba(29,158,117,0.25)" />
      {/* Row 2 — amber */}
      <circle cx="70" cy="78" r="5" fill="#F59E0B" />
      <rect x="80" y="74" width="40" height="8" rx="3" fill="rgba(245,158,11,0.25)" />
      {/* Row 3 — green */}
      <circle cx="70" cy="96" r="5" fill="#1D9E75" />
      <rect x="80" y="92" width="45" height="8" rx="3" fill="rgba(29,158,117,0.25)" />
    </IconFrame>
  )
}

// ─── Step 5: Alerts — bell + email + whatsapp ─────────────────────────────────

function IllustrationAlerts() {
  return (
    <IconFrame>
      {/* Bell body */}
      <path d="M100 42 C88 42 80 51 80 62 L80 76 L72 82 L128 82 L120 76 L120 62 C120 51 112 42 100 42 Z"
        fill="rgba(29,158,117,0.15)" stroke="#1D9E75" strokeWidth="2" strokeLinejoin="round" />
      {/* Bell clapper */}
      <path d="M94 82 C94 85.3 96.7 88 100 88 C103.3 88 106 85.3 106 82"
        stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Notification dot — live green */}
      <circle cx="119" cy="46" r="7" fill="#0A1810" />
      <circle cx="119" cy="46" r="5" fill="#1D9E75" />
      {/* Email pill — bottom left */}
      <rect x="58" y="96" width="36" height="20" rx="8" fill="rgba(29,158,117,0.15)" stroke="rgba(29,158,117,0.4)" strokeWidth="1" />
      <path d="M64 103 L76 103 M64 107 L72 107" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" />
      {/* WhatsApp pill — bottom right */}
      <rect x="106" y="96" width="36" height="20" rx="8" fill="rgba(37,211,102,0.12)" stroke="rgba(37,211,102,0.3)" strokeWidth="1" />
      <circle cx="124" cy="106" r="5" stroke="#25D166" strokeWidth="1.2" fill="none" />
      <path d="M121 104 C121 103 122 102 124 102 C126 102 127 103 127 104 C127 105.2 126.4 106 125.4 106.4 L125.5 108"
        stroke="#25D166" strokeWidth="1" strokeLinecap="round" fill="none" />
    </IconFrame>
  )
}

// ─── Guide steps definition ────────────────────────────────────────────────────

interface GuideStep {
  illustration: React.ReactNode
  titleKey: string
  descKey: string
}

const STEPS: GuideStep[] = [
  { illustration: <IllustrationWelcome />,   titleKey: 'guide.step1.title', descKey: 'guide.step1.desc' },
  { illustration: <IllustrationAddClient />, titleKey: 'guide.step2.title', descKey: 'guide.step2.desc' },
  { illustration: <IllustrationRecipient />, titleKey: 'guide.step3.title', descKey: 'guide.step3.desc' },
  { illustration: <IllustrationDashboard />, titleKey: 'guide.step4.title', descKey: 'guide.step4.desc' },
  { illustration: <IllustrationAlerts />,    titleKey: 'guide.step5.title', descKey: 'guide.step5.desc' },
]

// ─── Main Component ────────────────────────────────────────────────────────────

interface OnboardingGuideProps {
  officeId: string | null
  onDismiss: () => void
}

export function OnboardingGuide({ officeId, onDismiss }: OnboardingGuideProps) {
  const [step, setStep] = useState(0)
  const { t } = useLang()

  const current = STEPS[step]
  const isFirst = step === 0
  const isLast  = step === STEPS.length - 1

  function dismiss() {
    if (officeId) {
      try { localStorage.setItem(`guide_seen_${officeId}`, '1') } catch {}
    }
    onDismiss()
  }

  function handleNext() { if (isLast) { dismiss(); return }; setStep(s => s + 1) }
  function handleBack() { setStep(s => s - 1) }

  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).dataset.backdrop === 'true') dismiss()
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.70)', backdropFilter: 'blur(6px)' }}
      data-backdrop="true"
      onClick={handleBackdrop}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ backgroundColor: '#0E1412', border: '1px solid rgba(29,158,117,0.18)', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}
      >
        {/* Header: dots + skip */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0">
          <div className="flex items-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className="transition-all rounded-full"
                style={{
                  width: i === step ? 24 : 7,
                  height: 7,
                  backgroundColor: i === step
                    ? '#1D9E75'
                    : i < step
                    ? 'rgba(29,158,117,0.40)'
                    : 'rgba(255,255,255,0.10)',
                }}
              />
            ))}
          </div>
          <button
            onClick={dismiss}
            className="text-xs px-2.5 py-1 rounded-lg transition hover:opacity-80"
            style={{ color: 'rgba(255,255,255,0.30)', backgroundColor: 'rgba(255,255,255,0.05)' }}
          >
            {t('guide.skip')}
          </button>
        </div>

        {/* Illustration */}
        <div className="px-6 pt-4 pb-0">
          {current.illustration}
        </div>

        {/* Text */}
        <div className="px-6 pb-6 pt-2">
          <span
            className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-3"
            style={{ backgroundColor: 'rgba(29,158,117,0.15)', color: '#1D9E75' }}
          >
            {t('guide.stepLabel', { n: String(step + 1), total: String(STEPS.length) })}
          </span>
          <h2 className="text-white font-bold text-[22px] leading-tight mb-2">
            {t(current.titleKey)}
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.50)' }}>
            {t(current.descKey)}
          </p>

          {/* Nav buttons */}
          <div className="flex items-center gap-3">
            {!isFirst ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition hover:opacity-80"
                style={{ color: 'rgba(255,255,255,0.40)', backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {t('guide.back')}
              </button>
            ) : (
              <div className="flex-1" />
            )}

            <button
              onClick={handleNext}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition hover:opacity-90 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#1D9E75' }}
            >
              {isLast ? t('guide.done') : t('guide.next')}
              {!isLast && (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useShowGuide(officeId: string | null, onboardingCompleted: boolean): boolean {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!officeId || !onboardingCompleted) return
    try {
      const seen = localStorage.getItem(`guide_seen_${officeId}`)
      if (!seen) setShow(true)
    } catch { /* localStorage unavailable */ }
  }, [officeId, onboardingCompleted])

  return show
}
